import { useState } from 'react';
import {
  Container, Title, Text, Stack, Group, Badge, Button, Paper,
  TextInput, Textarea, Modal, Skeleton, Center, Alert,
  ActionIcon, Tooltip, Combobox, useCombobox, InputBase, ScrollArea,
  Tabs,
} from '@mantine/core';
import {
  IconUser, IconCheck, IconX, IconEdit, IconAlertTriangle,
  IconRefresh, IconBuilding, IconPlus, IconUserCheck, IconHistory,
  IconClock,
} from '@tabler/icons-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../lib/api';
import { customerLabel } from '../lib/weclappCustomer';
import type { ContactSuggestion, ContactSuggestionSource } from '../lib/api/domains/contactSuggestions';

function statusColor(s: ContactSuggestion['status']) {
  if (s === 'PENDING') return 'orange';
  if (s === 'APPROVED') return 'teal';
  return 'gray';
}

function statusLabel(s: ContactSuggestion['status']) {
  if (s === 'PENDING') return 'Offen';
  if (s === 'APPROVED') return 'Genehmigt';
  return 'Abgelehnt';
}

function sourceColor(s: ContactSuggestionSource) {
  if (s === 'TECHNICIAN') return 'blue';
  if (s === 'AUTO') return 'violet';
  return 'orange';
}

function sourceLabel(s: ContactSuggestionSource) {
  if (s === 'TECHNICIAN') return 'Intern';
  if (s === 'AUTO') return 'Automatisch';
  return 'Kunde';
}

function KindBadge({ kind }: { kind: ContactSuggestion['kind'] }) {
  if (kind === 'UPDATE') {
    return (
      <Badge size="xs" color="blue" variant="light" leftSection={<IconRefresh size={10} />}>
        Aktualisierung
      </Badge>
    );
  }
  return <Badge size="xs" color="teal" variant="light">Neuer Kontakt</Badge>;
}

// ── Review-Modal ──────────────────────────────────────────────────────────────
function ReviewModal({ suggestion, onClose }: { suggestion: ContactSuggestion; onClose: () => void }) {
  const qc = useQueryClient();
  const isUpdate = suggestion.kind === 'UPDATE';
  const [name, setName] = useState(suggestion.name ?? '');
  const [email, setEmail] = useState(suggestion.email ?? '');
  const [phone, setPhone] = useState(suggestion.phone ?? '');
  const [reviewNote, setReviewNote] = useState('');

  const invalidate = () => qc.invalidateQueries({ queryKey: ['contact-suggestions'] });

  const approveMut = useMutation({
    mutationFn: () =>
      api.approveContactSuggestion(suggestion.id, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        reviewNote: reviewNote.trim() || undefined,
      }),
    onSuccess: () => {
      const label = name.trim() || email.trim();
      notifications.show({
        color: 'teal',
        title: isUpdate ? 'Aktualisierung bestätigt' : 'Genehmigt',
        message: isUpdate ? `Daten von ${label} wurden aktualisiert.` : `${label} wurde in CW/Weclapp angelegt.`,
      });
      invalidate();
      onClose();
    },
    onError: (e) => {
      notifications.show({ color: 'red', title: 'Fehler', message: e instanceof Error ? e.message : 'Unbekannter Fehler' });
    },
  });

  const rejectMut = useMutation({
    mutationFn: () => api.rejectContactSuggestion(suggestion.id, { reviewNote: reviewNote.trim() || undefined }),
    onSuccess: () => {
      notifications.show({ color: 'gray', title: 'Abgelehnt', message: 'Vorschlag wurde abgelehnt.' });
      invalidate();
      onClose();
    },
    onError: (e) => {
      notifications.show({ color: 'red', title: 'Fehler', message: e instanceof Error ? e.message : 'Unbekannter Fehler' });
    },
  });

  const busy = approveMut.isPending || rejectMut.isPending;
  const canApprove = name.trim().length >= 2 && email.trim().includes('@');

  return (
    <Modal
      opened
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconUserCheck size={16} />
          <Text fw={600}>{isUpdate ? 'Daten-Aktualisierung prüfen' : 'Ansprechpartner prüfen'}</Text>
          <KindBadge kind={suggestion.kind} />
        </Group>
      }
      size="md"
    >
      <Stack gap="sm">
        <Paper p="sm" withBorder radius="sm" bg="gray.0">
          <Group gap="xs">
            <IconBuilding size={14} color="gray" />
            <Text size="sm" fw={500}>{suggestion.customer.name}</Text>
          </Group>
          {suggestion.note && (
            <Text size="xs" c="dimmed" mt={4}>„{suggestion.note}"</Text>
          )}
        </Paper>

        <TextInput
          label="Name"
          required={!isUpdate}
          value={name}
          onChange={(e) => setName(e.currentTarget.value)}
        />
        <TextInput
          label="E-Mail"
          required
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
        />
        <TextInput
          label="Telefon"
          value={phone}
          onChange={(e) => setPhone(e.currentTarget.value)}
        />
        <Textarea
          label="Interne Notiz (optional)"
          placeholder="Warum genehmigt / abgelehnt?"
          minRows={2}
          value={reviewNote}
          onChange={(e) => setReviewNote(e.currentTarget.value)}
        />

        <Group justify="space-between" mt="xs">
          <Button
            variant="subtle"
            color="red"
            leftSection={<IconX size={14} />}
            loading={rejectMut.isPending}
            disabled={busy}
            onClick={() => rejectMut.mutate()}
          >
            Ablehnen
          </Button>
          <Button
            color={isUpdate ? 'blue' : 'teal'}
            leftSection={<IconCheck size={14} />}
            loading={approveMut.isPending}
            disabled={busy || !canApprove}
            onClick={() => approveMut.mutate()}
          >
            {isUpdate ? 'Aktualisierung bestätigen' : 'Genehmigen & in CW/Weclapp anlegen'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Neuer-Vorschlag-Modal (Techniker) ─────────────────────────────────────────
function NewSuggestionModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const combobox = useCombobox({ onDropdownClose: () => combobox.resetSelectedOption() });

  const [customerId, setCustomerId] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [note, setNote] = useState('');

  const { data: customersData } = useQuery({
    queryKey: ['customers-list-for-suggestion', customerSearch],
    queryFn: () => api.listCustomers({ q: customerSearch || undefined, isActive: true, limit: 50 }),
    staleTime: 30_000,
  });

  const customers = customersData?.items ?? [];
  const filteredOptions = customerSearch
    ? customers.filter(
        (c) =>
          c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
          (c.customerNumber ?? '').toLowerCase().includes(customerSearch.toLowerCase())
      )
    : customers;

  const createMut = useMutation({
    mutationFn: () =>
      api.createContactSuggestion({
        customerId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || undefined,
        note: note.trim() || undefined,
      }),
    onSuccess: () => {
      notifications.show({ color: 'blue', title: 'Vorschlag erstellt', message: `${name} wurde als interner Vorschlag eingereicht.` });
      qc.invalidateQueries({ queryKey: ['contact-suggestions'] });
      onClose();
    },
    onError: (e) => {
      notifications.show({ color: 'red', title: 'Fehler', message: e instanceof Error ? e.message : 'Unbekannter Fehler' });
    },
  });

  const canSubmit = customerId && name.trim().length >= 2 && email.trim().includes('@');

  return (
    <Modal
      opened
      onClose={onClose}
      title={<Group gap="xs"><IconPlus size={16} /><Text fw={600}>Neuen Vorschlag einreichen</Text></Group>}
      size="md"
    >
      <Stack gap="sm">
        <Combobox
          store={combobox}
          onOptionSubmit={(val) => {
            setCustomerId(val);
            const c = customers.find((x) => x.id === val);
            setCustomerSearch(c?.name ?? '');
            combobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              label="Kunde"
              required
              placeholder="Kundenname suchen…"
              value={customerSearch}
              onChange={(e) => {
                setCustomerSearch(e.currentTarget.value);
                setCustomerId('');
                combobox.openDropdown();
              }}
              onClick={() => combobox.openDropdown()}
              onFocus={() => combobox.openDropdown()}
              rightSection={<Combobox.Chevron />}
              rightSectionPointerEvents="none"
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              <ScrollArea.Autosize mah={200} type="scroll">
                {filteredOptions.length === 0 ? (
                  <Combobox.Empty>Kein Kunde gefunden</Combobox.Empty>
                ) : (
                  filteredOptions.map((c) => (
                    <Combobox.Option key={c.id} value={c.id}>{customerLabel(c.name, c.customerNumber)}</Combobox.Option>
                  ))
                )}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <TextInput label="Name" required placeholder="Max Mustermann" value={name} onChange={(e) => setName(e.currentTarget.value)} />
        <TextInput label="E-Mail" required placeholder="max@firma.de" value={email} onChange={(e) => setEmail(e.currentTarget.value)} />
        <TextInput label="Telefon (optional)" placeholder="+43 1 234 5678" value={phone} onChange={(e) => setPhone(e.currentTarget.value)} />
        <Textarea label="Hinweis (optional)" placeholder="Warum wird dieser Kontakt vorgeschlagen?" minRows={2} value={note} onChange={(e) => setNote(e.currentTarget.value)} />

        <Group justify="flex-end" mt="xs">
          <Button variant="subtle" onClick={onClose} disabled={createMut.isPending}>Abbrechen</Button>
          <Button leftSection={<IconPlus size={14} />} loading={createMut.isPending} disabled={!canSubmit} onClick={() => createMut.mutate()}>
            Vorschlag einreichen
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────────
function SuggestionCard({ suggestion, onReview }: { suggestion: ContactSuggestion; onReview: (s: ContactSuggestion) => void }) {
  return (
    <Paper withBorder radius="md" p="md">
      <Group justify="space-between" align="flex-start">
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group gap="xs" wrap="wrap">
            <IconUser size={14} color="var(--mantine-color-dimmed)" />
            <Text fw={600} size="sm" truncate>{suggestion.name ?? suggestion.email}</Text>
            <Badge size="xs" color={statusColor(suggestion.status)} variant="light">
              {statusLabel(suggestion.status)}
            </Badge>
            <KindBadge kind={suggestion.kind} />
            <Badge size="xs" color={sourceColor(suggestion.source)} variant="dot">
              {sourceLabel(suggestion.source)}
            </Badge>
          </Group>
          <Text size="xs" c="dimmed">{suggestion.email}{suggestion.phone ? ` · ${suggestion.phone}` : ''}</Text>
          <Group gap={4}>
            <IconBuilding size={12} color="var(--mantine-color-dimmed)" />
            <Text size="xs" c="dimmed">{suggestion.customer.name}</Text>
          </Group>
          {suggestion.note && (
            <Text size="xs" c="dimmed" fs="italic">„{suggestion.note}"</Text>
          )}
          {suggestion.reviewNote && (
            <Text size="xs" c="dimmed">Notiz: {suggestion.reviewNote}</Text>
          )}
          <Text size="xs" c="dimmed">
            {new Date(suggestion.createdAt).toLocaleDateString('de-DE')}
            {suggestion.reviewedAt && ` → ${new Date(suggestion.reviewedAt).toLocaleDateString('de-DE')}`}
          </Text>
        </Stack>
        {suggestion.status === 'PENDING' && (
          <Tooltip label="Prüfen">
            <ActionIcon variant="light" color="orange" onClick={() => onReview(suggestion)}>
              <IconEdit size={16} />
            </ActionIcon>
          </Tooltip>
        )}
      </Group>
    </Paper>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export function ContactSuggestionsPage() {
  const [tab, setTab] = useState<'pending' | 'history'>('pending');
  const [reviewing, setReviewing] = useState<ContactSuggestion | null>(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterCustomerSearch, setFilterCustomerSearch] = useState('');
  const filterCombobox = useCombobox({ onDropdownClose: () => filterCombobox.resetSelectedOption() });
  const qc = useQueryClient();

  const { data: customersData } = useQuery({
    queryKey: ['customers-list-for-suggestion-filter', filterCustomerSearch],
    queryFn: () => api.listCustomers({ q: filterCustomerSearch || undefined, isActive: true, limit: 80 }),
    staleTime: 30_000,
  });
  const allCustomers = customersData?.items ?? [];
  const filteredCustomerOptions = filterCustomerSearch
    ? allCustomers.filter((c) => c.name.toLowerCase().includes(filterCustomerSearch.toLowerCase()))
    : allCustomers;

  const pendingQuery = useQuery({
    queryKey: ['contact-suggestions', 'PENDING', filterCustomerId],
    queryFn: () => api.getContactSuggestions({ status: 'PENDING', customerId: filterCustomerId || undefined, limit: 200 }),
    staleTime: 30_000,
  });

  const historyQuery = useQuery({
    queryKey: ['contact-suggestions', 'HISTORY', filterCustomerId],
    queryFn: async () => {
      const [approved, rejected] = await Promise.all([
        api.getContactSuggestions({ status: 'APPROVED', customerId: filterCustomerId || undefined, limit: 200 }),
        api.getContactSuggestions({ status: 'REJECTED', customerId: filterCustomerId || undefined, limit: 200 }),
      ]);
      return {
        suggestions: [...approved.suggestions, ...rejected.suggestions].sort(
          (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
        total: approved.total + rejected.total,
      };
    },
    staleTime: 60_000,
    enabled: tab === 'history',
  });

  const pendingCount = pendingQuery.data?.suggestions.length ?? 0;
  const historyCount = historyQuery.data?.total ?? null;

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="flex-start">
          <div>
            <Group gap="xs">
              <Title order={2}>Ansprechpartner-Vorschläge</Title>
              {pendingCount > 0 && (
                <Badge color="orange" size="lg">{pendingCount} offen</Badge>
              )}
            </Group>
            <Text size="sm" c="dimmed" mt={4}>
              Von Kunden eingereichte Kontaktvorschläge prüfen und in CW / Weclapp anlegen.
            </Text>
          </div>
          <Group gap="xs">
            <Button leftSection={<IconPlus size={16} />} variant="light" onClick={() => setShowNewModal(true)}>
              Neuer Vorschlag
            </Button>
            <ActionIcon variant="subtle" size="lg" onClick={() => qc.invalidateQueries({ queryKey: ['contact-suggestions'] })}>
              <IconRefresh size={18} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Kunden-Filter */}
        <Combobox
          store={filterCombobox}
          onOptionSubmit={(val) => {
            if (val === '__all__') {
              setFilterCustomerId('');
              setFilterCustomerSearch('');
            } else {
              setFilterCustomerId(val);
              const c = allCustomers.find((x) => x.id === val);
              setFilterCustomerSearch(c?.name ?? '');
            }
            filterCombobox.closeDropdown();
          }}
        >
          <Combobox.Target>
            <InputBase
              placeholder="Alle Kunden"
              value={filterCustomerSearch}
              leftSection={<IconBuilding size={14} />}
              rightSection={
                filterCustomerId ? (
                  <ActionIcon
                    size="xs"
                    variant="subtle"
                    color="gray"
                    onClick={() => { setFilterCustomerId(''); setFilterCustomerSearch(''); }}
                  >
                    <IconX size={12} />
                  </ActionIcon>
                ) : (
                  <Combobox.Chevron />
                )
              }
              rightSectionPointerEvents={filterCustomerId ? 'all' : 'none'}
              style={{ maxWidth: 280 }}
              onChange={(e) => {
                setFilterCustomerSearch(e.currentTarget.value);
                setFilterCustomerId('');
                filterCombobox.openDropdown();
              }}
              onClick={() => filterCombobox.openDropdown()}
              onFocus={() => filterCombobox.openDropdown()}
            />
          </Combobox.Target>
          <Combobox.Dropdown>
            <Combobox.Options>
              <ScrollArea.Autosize mah={220} type="scroll">
                <Combobox.Option value="__all__">
                  <Text size="sm" c="dimmed" fs="italic">Alle Kunden anzeigen</Text>
                </Combobox.Option>
                {filteredCustomerOptions.length === 0 ? (
                  <Combobox.Empty>Kein Kunde gefunden</Combobox.Empty>
                ) : (
                  filteredCustomerOptions.map((c) => (
                    <Combobox.Option key={c.id} value={c.id}>{c.name}</Combobox.Option>
                  ))
                )}
              </ScrollArea.Autosize>
            </Combobox.Options>
          </Combobox.Dropdown>
        </Combobox>

        <Tabs value={tab} onChange={(v) => setTab((v ?? 'pending') as typeof tab)}>
          <Tabs.List>
            <Tabs.Tab value="pending" leftSection={<IconClock size={14} />}>
              Offen
              {pendingCount > 0 && (
                <Badge size="xs" color="orange" ml={6}>{pendingCount}</Badge>
              )}
            </Tabs.Tab>
            <Tabs.Tab value="history" leftSection={<IconHistory size={14} />}>
              Verlauf
              {historyCount != null && historyCount > 0 && (
                <Badge size="xs" color="gray" variant="light" ml={6}>{historyCount}</Badge>
              )}
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="pending" pt="md">
            {pendingQuery.isLoading && (
              <Stack gap="sm">
                {[1, 2, 3].map((i) => <Skeleton key={i} height={90} radius="md" />)}
              </Stack>
            )}
            {pendingQuery.isError && (
              <Alert icon={<IconAlertTriangle size={16} />} color="red">
                Vorschläge konnten nicht geladen werden.
              </Alert>
            )}
            {!pendingQuery.isLoading && !pendingQuery.isError && pendingCount === 0 && (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <IconUser size={32} color="var(--mantine-color-dimmed)" />
                  <Text c="dimmed" size="sm">Keine offenen Vorschläge.</Text>
                </Stack>
              </Center>
            )}
            <Stack gap="sm">
              {pendingQuery.data?.suggestions.map((s) => (
                <SuggestionCard key={s.id} suggestion={s} onReview={setReviewing} />
              ))}
            </Stack>
          </Tabs.Panel>

          <Tabs.Panel value="history" pt="md">
            {historyQuery.isLoading && (
              <Stack gap="sm">
                {[1, 2, 3].map((i) => <Skeleton key={i} height={90} radius="md" />)}
              </Stack>
            )}
            {historyQuery.isError && (
              <Alert icon={<IconAlertTriangle size={16} />} color="red">
                Verlauf konnte nicht geladen werden.
              </Alert>
            )}
            {!historyQuery.isLoading && !historyQuery.isError && (historyQuery.data?.suggestions.length ?? 0) === 0 && (
              <Center py="xl">
                <Stack align="center" gap="xs">
                  <IconHistory size={32} color="var(--mantine-color-dimmed)" />
                  <Text c="dimmed" size="sm">Noch kein Verlauf vorhanden.</Text>
                </Stack>
              </Center>
            )}
            <Stack gap="sm">
              {historyQuery.data?.suggestions.map((s) => (
                <SuggestionCard key={s.id} suggestion={s} onReview={setReviewing} />
              ))}
            </Stack>
          </Tabs.Panel>
        </Tabs>

        {showNewModal && (
          <NewSuggestionModal onClose={() => setShowNewModal(false)} />
        )}

        {reviewing && (
          <ReviewModal suggestion={reviewing} onClose={() => setReviewing(null)} />
        )}
      </Stack>
    </Container>
  );
}
